(() => {
  if (customElements.get("request-info-popup")) return;

  class RequestInfoPopup extends HTMLElement {
    constructor() {
      super();
      this.formId = this.getAttribute("data-form-id");
      this.phoneInput = null;
      this.intlTelInputInstance = null;
      this.triggerType = this.getAttribute("data-trigger") || "exit_intent";
      this.triggerPage = this.getAttribute("data-trigger-page");
      this.delaySeconds = parseInt(this.getAttribute("data-delay")) || 30;
      this.isTriggered = false;
      this.form = null;
      this.focusableElements = null;
      this.previousFocusedElement = null;

      this.handleMouseLeave = this.handleMouseLeave.bind(this);
      this.handleCloseClick = this.handleCloseClick.bind(this);
      this.handleOverlayClick = this.handleOverlayClick.bind(this);
      this.handleKeyDown = this.handleKeyDown.bind(this);
      this.handleFocusTrap = this.handleFocusTrap.bind(this);
      this.handleThemeEditorEvents = this.handleThemeEditorEvents.bind(this);
    }

    connectedCallback() {
      this.form = this.querySelector("[data-form]");
      this.initializePhoneInput();
      this.initializeCountryAreaLogic();
      this.setEventListeners();

      if (window.location.hash === `#${this.formId}`) {
        this.showFormFeedback();
        return;
      }

      if (this.triggerType === "delay") {
        this.handleDelayFirstVisit();
      } else {
        const wasShownSuccessfully = sessionStorage.getItem(
          `request-info-popup-shown-${this.formId}`
        );
        if (wasShownSuccessfully) return;

        const shouldShowFromPageExit = sessionStorage.getItem(
          `request-info-popup-page-exit-${this.formId}`
        );
        if (shouldShowFromPageExit) {
          sessionStorage.removeItem(
            `request-info-popup-page-exit-${this.formId}`
          );
          this.triggerPopup();
          return;
        }

        this.initializeTriggers();
      }
    }

    handleDelayFirstVisit() {
      const localKey = `request-info-popup-first-visit-${this.formId}`;
      const sessionKey = `request-info-popup-first-visit-session-${this.formId}`;
      const cookieKey = `request-info-popup-first-visit-cookie-${this.formId}`;

      const cookieExists = document.cookie
        .split("; ")
        .some((row) => row.startsWith(cookieKey));

      const isFirstVisit =
        !localStorage.getItem(localKey) ||
        !sessionStorage.getItem(sessionKey) ||
        !cookieExists;

      if (!localStorage.getItem(localKey)) localStorage.setItem(localKey, "first-visit");
      if (!sessionStorage.getItem(sessionKey)) sessionStorage.setItem(sessionKey, "first-visit");
      if (!cookieExists)
        document.cookie = `${cookieKey}=first-visit; path=/; max-age=${60 * 60 * 24 * 365}`;
  
      if (isFirstVisit) {
        setTimeout(() => {
          if (!this.isTriggered) {
            this.triggerPopup();
          
            localStorage.setItem(localKey, "first-visit");
            sessionStorage.setItem(sessionKey, "first-visit");
            document.cookie = `${cookieKey}=first-visit; path=/; max-age=${60 * 60 * 24 * 365}`;
          }
        }, this.delaySeconds * 1000);
      }
    }

    disconnectedCallback() {
      this.removeEventListeners();
      this.destroyPhoneInput();
    }

    initializePhoneInput() {
      this.phoneInput = this.querySelector("[data-phone-input]");

      if (this.phoneInput && window.intlTelInput) {
        this.intlTelInputInstance = window.intlTelInput(this.phoneInput, {
          initialCountry: "ae",
          formatOnDisplay: true,
          loadUtils: () =>
            import(
              "https://cdn.jsdelivr.net/npm/intl-tel-input@25.3.1/build/js/utils.js"
            ),
        });

        this.phoneInput.addEventListener("countrychange", () => {
          const { dialCode } =
            this.intlTelInputInstance.getSelectedCountryData();
          if (!dialCode) return;
          if (this.phoneInput.value.startsWith(`+${dialCode}`)) return;

          this.phoneInput.value = `+${dialCode}`;
        });

        const formField = this.phoneInput.closest("[data-form-field]");
        if (formField)
          formField.classList.add("request-info-popup__form-field--phone");
      }
    }

    destroyPhoneInput() {
      if (this.intlTelInputInstance) {
        this.intlTelInputInstance.destroy();
        this.intlTelInputInstance = null;
      }
    }

    initializeCountryAreaLogic() {
      this.countrySelect = this.querySelector("[data-country-select]");
      this.areaSelect = this.querySelector("[data-area-select]");
      this.areaField = this.areaSelect?.closest("[data-form-field]");

      this.handleCountryChange = this.handleCountryChange.bind(this);

      if (this.countrySelect) {
        this.prioritizeCountries(["United Arab Emirates", "Saudi Arabia"]);
      }

      if (this.countrySelect && this.areaSelect && this.areaField)
        this.updateAreaField();
    }

    prioritizeCountries(countries) {
      const options = Array.from(this.countrySelect.options);
      const firstOption = this.countrySelect.options[0];

      countries.reverse().forEach((countryName, index) => {
        const option = options.find(
          (opt) => opt.value.toLowerCase() === countryName.toLowerCase()
        );
        if (option) {
          this.countrySelect.insertBefore(option, firstOption.nextSibling);
        }
      });
    }

    updateAreaField() {
      const selectedOption = this.countrySelect.selectedOptions[0];
      const provinces = selectedOption
        ? JSON.parse(selectedOption.dataset.provinces || "[]")
        : [];


        
      if (!provinces.length && (selectedOption.value == "Saudi Arabia" || selectedOption.value == "المملكة العربية السعودية")) {
        let ksa_provinces = ["Abha", "Abu Areish", "Adham", "Afif", "Aflaj", "Ahad Rufaidah", "Al Hassa", "Alrass", "Anak", "Aqiq", "Ar Rafi'Ah", "Arar", "Badaya", "Bader", "Baha", "Baqiq", "Biljurashi", "Bish", "Bisha", "Bukeiriah", "Buraidah", "Dammam", "Darb", "Dawadmi", "Dere'Iyeh", "Dhahran", "Dhahran Al Janoob", "Domat Al Jandal", "Duba", "Gilwa", "Jazan", "Gizan", "Hafer Al Batin", "Hail", "Hawtat Bani Tamim", "Hinakeya", "Hofuf", "Jeddah", "Jouf", "Jubail", "Jumum", "Khafji", "Khamis Mushait", "Kharj", "Khobar", "Khulais", "Khurma", "Laith", "Madinah", "Mahad Al Dahab", "Majarda", "Majma", "Makkah", "Mandak", "Midinhab", "Mikhwa", "Mohayel Aseer", "Muzahmiah", "Najran", "Namas", "Noweirieh", "Onaiza", "Al Ula", "Oyoon Al Jawa", "Qariya Al Olaya", "Qassim", "Qatif", "Qunfudah", "Qurayat", "Quwei'Ieh", "Rabigh", "Rafha", "Rania", "Ras Tanura", "Rejal Almaa", "Remah", "Riyadh", "Riyadh Al Khabra", "Sabt El Alaya", "Sabya", "Safwa", "Sajir", "Sakaka", "Samtah", "Sarat Obeida", "Seihat", "Shaqra", "Sharourah", "Sulaiyl", "Tabrjal", "Tabuk", "Taif", "Tanumah", "Tarut", "Tatleeth", "Tayma", "Thuqba", "Towal", "Turaib", "Turaif", "Turba", "Um Aljamajim", "Wadi El Dwaser", "Wajeh (Al Wajh)", "Yanbu", "Zulfi"]
        if (document.documentElement.getAttribute('lang') == "ar") {
          ksa_provinces = ["أبها", "أبو عريش", "أضم", "عفيف", "الأفلاج", "أحد رفيدة", "الأحساء", "الرس", "عنك", "العقيق", "الرفيعة", "عرعر", "البدائع", "بدر", "الباحة", "بقيق", "بلجرشي", "بيش", "بيشة", "البكيرية", "بريدة", "الدمام", "الدرب", "الدوادمي", "الدرعية", "الظهران", "ظهران الجنوب", "دومة الجندل", "ضبا", "قلوة", "جازان", "جيزان", "حفر الباطن", "حائل", "حوطة بني تميم", "الحناكية", "الهفوف", "جدة", "الجوف", "الجبيل", "الجموم", "الخفجي", "خميس مشيط", "الخرج", "الخبر", "خليص", "الخرمة", "الليث", "المدينة المنورة", "مهد الذهب", "المجاردة", "المجمعة", "مكة المكرمة", "المندق", "المذنب", "المخواة", "محايل عسير", "المزاحمية", "نجران", "النماص", "النويرية", "عنيزة", "العلا", "عيون الجواء", "القرية العليا", "القصيم", "القطيف", "القنفذة", "القريات", "القويعية", "رابغ", "رفحاء", "رنية", "رأس تنورة", "رجال ألمع", "رماح", "الرياض", "رياض الخبراء", "سبت العلاية", "صبيا", "صفوى", "ساجر", "سكاكا", "صامطة", "سراة عبيدة", "سيهات", "شقراء", "شرورة", "السليل", "طبرجل", "تبوك", "الطائف", "تنومة", "تاروت", "تثليث", "تيماء", "الثقبة", "الطوال", "الطرائف", "طريف", "تربة", "أم الجماجم", "وادي الدواسر", "الوجه", "ينبع", "الزلفي"]
        }
        ksa_provinces.forEach(item => {
          provinces.push([`${item}`,`${item}`])
        })
      }

      const areaLabel = this.getAttribute("data-area-label") || "Area*";
      this.areaSelect.innerHTML = `<option value="" disabled selected>${areaLabel}</option>`;

      if (provinces.length > 0) {
        provinces.forEach(([value, label]) => {
          const option = document.createElement("option");
          option.value = value;
          option.textContent = label;
          this.areaSelect.appendChild(option);
        });

        this.areaField.style.display = "";
        this.areaSelect.required = true;
      } else if (selectedOption && selectedOption.value !== "---") {
        this.areaField.style.display = "none";
        this.areaSelect.required = false;
      } else {
        this.areaField.style.display = "";
        this.areaSelect.required = true;
      }
    }

    handleCountryChange() {
      this.updateAreaField();
    }

    setEventListeners() {
      this.countrySelect?.addEventListener("change", this.handleCountryChange);

      const closeButton = this.querySelector("[data-close-button]");
      closeButton?.addEventListener("click", this.handleCloseClick);

      this.addEventListener("click", this.handleOverlayClick);

      if (window.Shopify?.designMode) {
        document.addEventListener(
          "shopify:section:select",
          this.handleThemeEditorEvents
        );
        document.addEventListener(
          "shopify:section:deselect",
          this.handleThemeEditorEvents
        );
      }
    }

    removeEventListeners() {
      this.countrySelect?.removeEventListener(
        "change",
        this.handleCountryChange
      );

      const closeButton = this.querySelector("[data-close-button]");
      closeButton?.removeEventListener("click", this.handleCloseClick);

      this.removeEventListener("click", this.handleOverlayClick);

      document.removeEventListener("keydown", this.handleKeyDown);
      document.documentElement.removeEventListener(
        "mouseout",
        this.handleMouseLeave
      );

      if (window.Shopify?.designMode) {
        document.removeEventListener(
          "shopify:section:select",
          this.handleThemeEditorEvents
        );
        document.removeEventListener(
          "shopify:section:deselect",
          this.handleThemeEditorEvents
        );
      }
    }

    initializeTriggers() {
      if (window.Shopify?.designMode) return;

      switch (this.triggerType) {
        case "exit_intent":
          this.initializeExitIntent();
          break;
        case "page_exit":
          this.initializePageExit();
          break;
        case "delay":
          break;
      }
    }

    initializeExitIntent() {
      document.documentElement.addEventListener(
        "mouseout",
        this.handleMouseLeave
      );
    }

    initializePageExit() {
      if (!this.triggerPage) return;

      try {
        const triggerUrl = new URL(this.triggerPage, window.location.origin);
        if (window.location.pathname === triggerUrl.pathname) {
          sessionStorage.setItem(
            `request-info-popup-page-exit-${this.formId}`,
            "true"
          );
        }
      } catch (error) {
        console.warn("Invalid trigger page URL:", this.triggerPage);
      }
    }

    handleMouseLeave(event) {
      if ((event.clientY <= 0 || !event.relatedTarget) && !this.isTriggered) {
        this.triggerPopup();
      }
    }

    handleCloseClick() {
      this.close();
    }

    handleOverlayClick(event) {
      if (event.target === this) this.close();
    }

    handleKeyDown(event) {
      if (event.key === "Escape") {
        this.close();
        return;
      }
      this.handleFocusTrap(event);
    }

    handleFocusTrap(event) {
      if (event.key !== "Tab") return;
      if (!this.focusableElements) return;

      const firstFocusable = this.focusableElements[0];
      const lastFocusable =
        this.focusableElements[this.focusableElements.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === firstFocusable) {
          event.preventDefault();
          lastFocusable.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          event.preventDefault();
          firstFocusable.focus();
        }
      }
    }

    getFocusableElements() {
      const focusableSelectors = [
        "button:not([disabled])",
        "input:not([disabled])",
        "select:not([disabled])",
        "textarea:not([disabled])",
        '[tabindex]:not([tabindex="-1"]):not([disabled])',
        "a[href]",
      ];

      return Array.from(
        this.querySelectorAll(focusableSelectors.join(", "))
      ).filter((element) => {
        return element.offsetWidth > 0 && element.offsetHeight > 0;
      });
    }

    handleThemeEditorEvents(event) {
      const sectionId = this.getAttribute("data-section-id");
      const eventSectionId = event.detail?.sectionId;

      if (
        event.type === "shopify:section:select" &&
        eventSectionId === sectionId
      ) {
        this.open();
      } else if (
        event.type === "shopify:section:deselect" &&
        eventSectionId === sectionId
      ) {
        this.close();
      }
    }

    showFormFeedback() {
      const formElement = this.form;
      const errorElement = formElement?.querySelector("[data-errors]");
      const hasErrors = errorElement && errorElement.textContent.trim() !== "";
      const hasSuccess = formElement?.querySelector("[data-success]");

      if (hasSuccess && !hasErrors) {
        sessionStorage.setItem(
          `request-info-popup-shown-${this.formId}`,
          "true"
        );
      }

      this.open();
    }

    triggerPopup() {
      if (!this.isTriggered) {
        this.isTriggered = true;
        this.open();
      }
    }

    open() {
      this.classList.add("request-info-popup--open");
      this.previousFocusedElement = document.activeElement;
      document.addEventListener("keydown", this.handleKeyDown);
      this.focusableElements = this.getFocusableElements();

      if (!Shopify.designMode){
        setTimeout(() => {
          const firstInput = this.querySelector("#request-info-popup-name");
          if (firstInput) {
            firstInput.focus();
          } else if (this.focusableElements.length > 0) {
            this.focusableElements[0].focus();
          }
        }, 100);
      }
    }

    close() {
      this.classList.remove("request-info-popup--open");
      document.removeEventListener("keydown", this.handleKeyDown);

      if (this.triggerType === "delay" && this.isTriggered) {
        const localKey = `request-info-popup-first-visit-${this.formId}`;
        const sessionKey = `request-info-popup-first-visit-session-${this.formId}`;
        const cookieKey = `request-info-popup-first-visit-cookie-${this.formId}`;
        localStorage.setItem(localKey, "first-visit");
        sessionStorage.setItem(sessionKey, "first-visit");
        document.cookie = `${cookieKey}=first-visit; path=/; max-age=${60 * 60 * 24 * 365}`;
      }

      if (
        this.previousFocusedElement &&
        document.body.contains(this.previousFocusedElement)
      ) {
        this.previousFocusedElement.focus();
      }

      this.focusableElements = null;
      this.previousFocusedElement = null;
    }
  }

  customElements.define("request-info-popup", RequestInfoPopup);
})();
